<script setup lang="ts">
import jsQR, { type QRCode } from 'jsqr'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    interval?: number
    regexp?: RegExp
  }>(),
  {
    interval: 500,
    regexp: undefined,
  }
)

const emit = defineEmits<{
  (e: 'detected', value: QRCode): void
}>()

const isValidData = (data: string) => {
  if (!props.regexp) {
    return true
  }
  return props.regexp.test(data)
}

const video = ref<HTMLVideoElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const obtainedCameraAccess = ref(false)

const { t } = useI18n()
const permissionStatusMessage = ref<string | null>(
  t('二次元コード読み取りを利用するためにはカメラへのアクセスを許可してください。')
)

let cameraStream: MediaStream
let intervalId: number | null

// video, canvasへのアクセスを伴うため mount後の実行を保証
onMounted(() => {
  navigator.mediaDevices
    .getUserMedia({
      audio: false,
      video: {
        facingMode: 'environment',
        frameRate: { ideal: 24 },
      },
    })
    .then((stream) => {
      cameraStream = stream

      if (video.value) {
        video.value.srcObject = cameraStream
        video.value.play().catch(() => {})
        drawVideoToCanvas()

        permissionStatusMessage.value = null
        obtainedCameraAccess.value = true
        intervalId = startScanInterval()
      }
    })
    .catch(() => {
      permissionStatusMessage.value = t(
        'カメラへのアクセスがブロックされました。アクセスを許可後、ページを再読み込みを実施してください。'
      )
    })
})

// コンポーネント破棄時にinterval、streamの停止を保証
onBeforeUnmount(() => {
  if (intervalId !== null) {
    clearInterval(intervalId)
  }
  if (cameraStream) {
    cameraStream.getTracks().forEach(function (track) {
      track.stop()
    })
  }
})

const startScanInterval = () => {
  return window.setInterval(() => {
    const result = scanQRCode()

    if (result) {
      if (!isValidData(result.data)) {
        showInvalidQRCodeMessage()
      } else {
        video.value?.pause()
        emit('detected', result)
      }
    }
  }, props.interval)
}

const invalidQRCode = ref(false)
const showInvalidQRCodeMessage = () => {
  invalidQRCode.value = true
  setTimeout(() => {
    invalidQRCode.value = false
  }, 3000)
}

let imgWidth: number
let imgHeight: number

const getCanvasContext = () => {
  return canvas.value?.getContext('2d', { willReadFrequently: true }) ?? null
}

const drawVideoToCanvas = () => {
  const ctx = getCanvasContext()
  if (!ctx || !video.value || !canvas.value) {
    // component破棄時はDOM消失により途中returnが行われるため requestAnimationFrameの再帰処理が終了する
    return
  }

  imgWidth = video.value.videoWidth
  imgHeight = video.value.videoHeight
  canvas.value.width = imgWidth
  canvas.value.height = imgHeight
  ctx.drawImage(video.value, 0, 0, imgWidth, imgHeight)

  // 参照: https://developer.mozilla.org/ja/docs/Web/API/window/requestAnimationFrame
  requestAnimationFrame(drawVideoToCanvas)
}

// ガイド枠のCSSサイズ
const GUIDE_SIZE = 160
const guideSizePx = `${GUIDE_SIZE}px`

const scanQRCode = (): QRCode | null => {
  const ctx = getCanvasContext()
  if (!ctx || !imgWidth || !imgHeight || !canvas.value) {
    return null
  }

  // 1. 画面上の表示サイズと実際の解像度の比率を計算
  const rect = canvas.value.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return null

  const scaleX = imgWidth / rect.width
  const scaleY = imgHeight / rect.height

  // 2. ガイド枠の「キャンバス（解像度）上でのサイズと位置」を計算
  const scanW = GUIDE_SIZE * scaleX
  const scanH = GUIDE_SIZE * scaleY
  const scanX = (imgWidth - scanW) / 2
  const scanY = (imgHeight - scanH) / 2

  // 3. ガイド枠の範囲をキャンバス内に clamp し整数化する(負値やキャンバス範囲外を許容しないため、範囲を安全に調整する)
  const clampedScanX = Math.floor(Math.max(0, Math.min(scanX, imgWidth)))
  const clampedScanY = Math.floor(Math.max(0, Math.min(scanY, imgHeight)))
  const clampedScanW = Math.floor(Math.min(scanW, imgWidth - clampedScanX))
  const clampedScanH = Math.floor(Math.min(scanH, imgHeight - clampedScanY))
  if (clampedScanW <= 0 || clampedScanH <= 0) {
    return null
  }

  // 4. 【重要】ガイド枠の範囲だけピクセルデータを取得
  // 全体ではなく、scanX, scanY から scanW, scanH の範囲のみを切り出す
  const imageData = ctx.getImageData(clampedScanX, clampedScanY, clampedScanW, clampedScanH)

  // 5. jsQRに渡す（jsQRは渡されたデータのサイズをもとに解析する）
  const code = jsQR(imageData.data, clampedScanW, clampedScanH)

  return code
}

const resume = () => {
  video.value?.play().catch(() => {})
}

defineExpose({ resume })
</script>

<template>
  <div class="is-relative">
    <Transition>
      <div v-if="invalidQRCode" class="notification overlay error-notification">
        <div class="is-flex">
          <p class="is-flex is-flex-grow-1 is-justify-content-center">
            <span v-t="'無効な二次元コードを読み取りました。'" class="notification-message" />
          </p>
          <button class="delete is-justify-content-end"></button>
        </div>
      </div>
    </Transition>
    <video v-show="false" ref="video" autoplay playsinline />
    <div v-if="!permissionStatusMessage" class="guide">
      <span class="corner top-left"></span>
      <span class="corner top-right"></span>
      <span class="corner bottom-left"></span>
      <span class="corner bottom-right"></span>
    </div>
    <canvas v-show="obtainedCameraAccess" ref="canvas" />
    <p v-if="permissionStatusMessage" class="message">
      {{ permissionStatusMessage }}
    </p>
  </div>
</template>

<style scoped lang="scss">
video,
canvas {
  width: 100%;
  height: 100%;
}

@include mobile {
  canvas {
    height: calc(95svh - 48px);
    object-fit: cover;
  }
}

.message {
  padding: 0.5rem;
  background-color: white;
  text-align: center;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.overlay {
  position: absolute;
  width: 100%;
  z-index: 1000;
  opacity: 0.8;
}

// bulma の is-danger を卒業した独自クラス（背景・文字色をトークンに）
// .notification 基盤（padding/radius/position 等）は bulma を継続利用
.notification.error-notification {
  background-color: var(--semantic-tokens-danger-strong-surface);
  color: var(--semantic-tokens-inverse-default-text);
}

.notification-message {
  font-weight: bold;
}

.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.guide {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: v-bind(guideSizePx);
  height: v-bind(guideSizePx);
  pointer-events: none;

  .corner {
    position: absolute;
    width: 20px; /* 角の線の長さ */
    height: 20px; /* 角の線の長さ */
    border: 4px solid white; /* 角の線の太さと色 */
  }

  /* 各角の不要な辺を消して L字 にする */
  .top-left {
    top: 0;
    left: 0;
    border-right: none;
    border-bottom: none;
  }

  .top-right {
    top: 0;
    right: 0;
    border-left: none;
    border-bottom: none;
  }

  .bottom-left {
    bottom: 0;
    left: 0;
    border-right: none;
    border-top: none;
  }

  .bottom-right {
    bottom: 0;
    right: 0;
    border-left: none;
    border-top: none;
  }
}
</style>
